<template>
	<scroll-frame v-if="visible" ref="itemsContainer" :class="frameClasses">
		<template #default>
			<table class="agenda" :class="[clazz, { 'inside-widgets': insideWidgets }]" ref="itemsTable">
				<tbody ref="itemsBody">
					<tr v-for="(ln, idx) in linearized" :key="ln.data._key" :class="idx%2 === 0 ? 'evenrow':'oddrow'">
						<td><project-node-head :node="ln.data" :level="ln.path.length-1" :project="ln.project" :readonly="ro || !hasAccess(ln.data, 'edit')" :modified="ln.data._mod"
											   @open="openTask(ln.path)" @navigate="navigateTask"></project-node-head></td>
						<td><user-party-set :users="ln.data.users"></user-party-set></td>
						<td class="subtask-date" :style="dateStyle(ln.data)">{{ formatTaskDate(ln.data.date) }}</td>
						<td v-if="!ro" class="subtask-del"><icon-button name="disconnect" @click.prevent="removeTask(ln)"></icon-button></td>
					</tr>
				</tbody>
			</table>
		</template>
		<template v-if="needTail" #tail>
			<div class="subtasks-controls">
				<span class="subtasks-add-buttons">
					<span><sym-icon name="task-add"></sym-icon> {{ env.msg('bas.add') }}:</span>
					<a href="#" @click.prevent="createNewNode(SrcType.TASK)">{{ env.msg('task.project.add.task') }}</a>
					<a href="#" @click.prevent="createNewNode(SrcType.CONTACT)">{{ env.msg('task.project.add.contact') }}</a>
					<a href="#" @click.prevent="createNewNode(SrcType.EVENT)">{{ env.msg('task.project.add.event') }}</a>
				</span>
				<span v-if="needSortControl">
					<select v-model="sort">
						<option value="manual">{{ env.msg('tasklist.sortManual') }}</option>
						<option value="priority">{{ env.msg('tasklist.sortPriority') }}</option>
						<option value="date">{{ env.msg('tasklist.sortDate') }}</option>
						<option value="name">{{ env.msg('tasklist.sortName') }}</option>
					</select>
				</span>
				<span class="subtasks-connect">
					<span><sym-icon name="task-search"></sym-icon> {{ env.msg('bas.attach') }}:</span>
					<suggester-part :part="attachSuggester" :data="attachData" :readonly="ro" :viewmode="vm" @suggested="attachmentSuggested" ref="suggesterComponent"></suggester-part>
				</span>
			</div>
		</template>
	</scroll-frame>
</template>

<script setup lang="ts">
import {ref, computed, type CSSProperties, reactive, onMounted, watch, toRaw} from "vue";
import useContainer, {type ContainerProps, containerProps} from "@fd/containers/Container.ts";
import {injectEnv} from "@fd/Environment.ts";
import ScrollFrame from "@fd/containers/agenda/ScrollFrame.vue";
import {Subtasks, TaskSort} from "@t/fd/layout/Subtasks.ts";
import SymIcon from "@fd/helpers/SymIcon.vue";
import {getter} from "@fd/Part.ts";
import {isTask, ProjectNode, Task} from "@t/task/Task.ts";
import {DateComparator, LinearizedNode, SequenceComparator, UnifiedProjectNode, ProjectNodeComparator, wrapNode, NameComparator, PriorityComparator, toSortableTree, sortTree, SortableNode} from "@fd/containers/subtasks/NodeUtils.ts";
import ProjectNodeHead from "@fd/containers/subtasks/ProjectNodeHead.vue";
import {parseDate, smartDate} from "@t/util/DateUtil.ts";
import UserPartySet from "@fd/containers/subtasks/UserPartySet.vue";
import SuggesterPart from "@fd/editors/suggester/SuggesterPart.vue";
import type {Suggester} from "@t/fd/editor/Suggester.ts";
import {loadSourceType, setEndpointId, SrcType} from "@t/meta/SourceType.ts";
import {hasAccess} from "@t/security/ObjectAccess.ts";
import {injectContext} from "@t/fd/AppContext.ts";
import IconButton from "@fd/helpers/IconButton.vue";

const uprops = defineProps({
	...containerProps
});
const props = uprops as ContainerProps;
const env = injectEnv();
const ctx = injectContext();

const st = computed<Subtasks>(() => props.part as Subtasks);
const {visible, visibilityEval, clazz, style, componentOfPart, partRef, localData, ro, vm, prop, formData} = useContainer(props);

const needTail = computed<boolean>(() => !ro.value);

const collapseMap = ref<Record<string, boolean>>({ });

const value = computed<ProjectNode[]|null|undefined>(() => {
	return getter(localData.value as FormData, prop.value, formData);
});

const rootArray = computed<any[]>(() => {
	const v = value.value;
	return Array.isArray(v) ? v : [];
});

const linearized = computed<LinearizedNode[]>(() => {
	console.log("linearized "+sort.value);
	const sortables = toSortableTree(rootArray.value, []);
	sortTree(sortables, comparator.value);
	const out:LinearizedNode[] = [];
	linearize(sortables, out);
	return out;
});

const sort = ref<TaskSort>(st.value.sort ?? "date");
const comparator = computed<ProjectNodeComparator>(() => {
	switch (sort.value) {
		case "manual": return SequenceComparator;
		case "priority": return PriorityComparator;
		case "name": return NameComparator;
		default: case "date": return DateComparator;
	}
});

const needSortControl = computed<boolean>(() => longestSequence(value.value) >= 2);

function linearize(nodes:SortableNode[], out:LinearizedNode[]):void {
	for (let i = 0; i < nodes.length; i++) {
		const sn = nodes[i];
		const wrapped = wrapNode(sn.original);
		const hasSubtasks = Array.isArray(sn.children) && sn.children.length > 0;
		out.push({ path: sn.path, original: sn.original, data: wrapped, project: hasSubtasks, collapsed: !!collapseMap.value[wrapped._key] });
		if (hasSubtasks) {
			linearize(sn.children, out);
		}
	}
}

const frameClasses = computed<string[]>(() => {
	const c:string[] = ["items"];
	if (props.insideWidgets) {
		c.push("subtasks-widget");
	}
	return c;
});

function formatTaskDate(date:string|null|undefined):string {
	if (date == null) return "";
	const parsed = parseDate(date);
	return parsed.date ? smartDate(parsed, env) : "";
}


function dateStyle(d:UnifiedProjectNode):CSSProperties {
	const s: CSSProperties = {};
	if (d.completed) s.color = "var(--agenda-fg-lc)";
	else {
		const parsed = parseDate(d.date);
		if (parsed.date && parsed.date.getTime() < Date.now()) s.color = "var(--red)";
	}
	return s;
};

const attachData = reactive({ id: 0, title: "", srcType: 0 });
const suggesterComponent = ref<InstanceType<typeof SuggesterPart> | null>(null);


function appendIds(all:string[], nodes:ProjectNode[]):void {
	if (Array.isArray(nodes)) {
		for (let i = 0; i < nodes.length; i++) {
			const pn = nodes[i];
			const nodeId = pn['id'];
			const nodeSrcType = pn['_type'];
			if (nodeId && nodeSrcType) {
				all.push(nodeSrcType + '-' + nodeId);
				if (isTask(pn)) appendIds(all, pn.subtasks);
			}
		}
	}
}

function extractIds():string[] {
	const all:string[] = [];
	// Find ID of the containing task
	const taskId = formData['id'];
	if (typeof taskId === 'number' && taskId > 0) all.push(SrcType.TASK + '-' + taskId);
	appendIds(all, value.value);
	return all;
}

const suggesterUrl = computed<string>(() => {
	let ep = "suggestfeed.do";
	const excluded:string[] = extractIds();
	if (excluded.length > 0) ep = env.net.urlParam(ep, "exclude", excluded.join(","));
	return ep;
});

const attachSuggester = computed<Suggester>(() => {
	return <Suggester>{
		type: "suggest",
		property: "id",
		minTags: 0,
		maxTags: 1,
		suggester: "ProjectNodeSuggester",
		url: suggesterUrl.value,
		propagate: [
			{
				d: "id",
				s: "id.main",
				c: "integer",
				empty: 0
			},
			{
				d: "title",
				s: "title.main",
				empty: ""
			},
			{
				d: "srcType",
				s: "id.srcType",
				c: "integer",
				empty: 0
			}
		],
		tag: [
			{
				d: "id",
				t: "id",
				empty: 0
			},
			{
				d: "title",
				t: "title",
				empty: "",
				optional: true
			},
			{
				d: "title",
				t: "name",
				empty: ""
			}
		]
	};
});

function maxSequence(nodes:ProjectNode[]):number {
	let max = 0;
	if (Array.isArray(nodes)) {
		for (let n of nodes) {
			const s = n.sequence;
			if (typeof s === "number" && s > max) max = s;
		}
	}
	return max;
}

function attachLoaded(node:ProjectNode):void {
	const max = maxSequence(value.value);
	node.sequence = max + 1;
	value.value.push(node);
	node['_mod'] = true;
}

async function attachProjectNode(srcType:number, id:number, title:string):Promise<void> {
	const type = await loadSourceType(env, srcType);
	let ep = setEndpointId("api"+type.edit, id);
	const resp = await env.net.fetch(ep);
	if (resp.ok) {
		const data = await resp.json();
		attachLoaded(data);
	}
}

function attachmentSuggested() {
	const id = attachData.id;
	const title = attachData.title;
	const srcType = attachData.srcType;
	attachProjectNode(srcType, id, title);

	attachData.id = 0;
	attachData.title = "";
	attachData.srcType = 0;

	setTimeout(() => {
		if (suggesterComponent.value) {
			suggesterComponent.value.focusLastEditor();
		}
	}, 200);
}

async function createNewNode(srcType:number):Promise<void> {
	const data = await env.create(true, false, srcType, ctx.value, null, "add");
	if (data) {
		attachLoaded(data as ProjectNode);
	}
}



// function sortNodes() {
// 	sortNodeList(value.value);
// }


function assignSequenceToNodeList(list:ProjectNode[]):void {
	if (Array.isArray(list)) {
		let seq = 1;
		for (let n of list) {
			const oldSeq = n.sequence;
			n.sequence = seq++;
			if (isTask(n)) {
				assignSequenceToNodeList(n.subtasks);
			}
			if (oldSeq !== n.sequence) n['_mod'] = true;
		}
	}
}

function longestSequence(nodes:ProjectNode[]):number {
	let len = 0;
	if (Array.isArray(nodes)) {
		len = nodes.length;
		for (let n of nodes) {
			if (isTask(n)) {
				const maxLen = longestSequence(n.subtasks);
				if (maxLen > len) len = maxLen;
			}
		}
	}
	return len;
}

function assignSequence() {
	assignSequenceToNodeList(value.value);
}

function findSubtask(path: number[]): ProjectNode | null {
	let nodeList: ProjectNode[] = value.value;
	let current: ProjectNode | null = null;

	for (let i = 0; i < path.length; i++) {
		const idx = path[i];
		if (idx < 0 || idx >= nodeList.length) return null;

		current = nodeList[idx];
		if ('subtasks' in current && Array.isArray((current as Task).subtasks)) {
			nodeList = (current as Task).subtasks;
		} else if (i < path.length - 1) {
			// Path too deep – next level expected, but no subtasks
			return null;
		}
	}
	return current;
}

function setProperties(a: Record<string, any>, b: Record<string, any>): void {
	// Convert b to non-reactive object to prevent copying proxies
	const rawB = toRaw(b);
	// Remove all existing properties in a
	for (const key in a) {
		if (Object.prototype.hasOwnProperty.call(a, key)) {
			delete a[key];
		}
	}

	// Copy properties from b to a
	for (const key in rawB) {
		if (Object.prototype.hasOwnProperty.call(b, key)) {
			a[key] = b[key];
		}
	}
}

async function openTask(path: number[]) {
	const pn = findSubtask(path);
	if (pn) {
		const oldData = structuredClone(toRaw(pn));
		const newData = await env.edit(true, false, pn._type, oldData, 0, "update");
		if (newData) {
			setProperties(pn, newData);
		}
	}
}

function navigateTask(srcType: number, id: number) {
	env.navigateTo(srcType, id);
}

function hasId(node: ProjectNode|UnifiedProjectNode):boolean {
	return typeof node['id'] === 'number' && node['id'] > 0;
}

/**
 * Return true if the removal of the node is just disconnection (user won't lose any in-memory data)
 */
function losslessRemoval(node:ProjectNode):boolean {
	if (!hasId(node)) return false;	// this is in-memory record
	// This record has ID (probably will be just disconnected), but it can have in-memory subtask
	if (isTask(node) && Array.isArray(node.subtasks)) {
		for (let st of node.subtasks) {
			if (!losslessRemoval(st)) return false;
		}
	}
	return true;
}

function removeTask(ln:LinearizedNode) {
	const path = ln.path;
	if (path.length === 1) {
		// The node is the direct subtask of this hosting task
		value.value.splice(path[0], 1);
	} else if (path.length > 1) {
		const parent = findSubtask(path.slice(0, -1));
		(parent as Task).subtasks.splice(path[path.length - 1], 1);
	}
}
</script>

<style>
.subtasks {
}

.subtasks-widget {
	display: grid;
	grid-column: 1 / -1;
	grid-template-columns: subgrid;
	align-items: baseline;
	/* row-gap: calc(2 * var(--form-v-padding)); */
	row-gap: 0;	/* No separation between head-body-tail */
	column-gap: calc(2 * var(--form-h-padding));
	position: relative;
	--next-left-gutter: var(--form-left-gutter);
	--next-right-gutter: var(--form-right-gutter);
	transition: border-color 500ms;

	margin-left: calc(-1 * var(--form-left-gutter));
	margin-right: calc(-1 * var(--form-right-gutter));
	border-top: 1px solid var(--border-color);

	& > .scroll-frame-head,
	& > .scroll-frame-body,
	& > .scroll-frame-tail {
		grid-column: var(--widget-start) / var(--widget-end);
	}
}

.subtask-date {
	font-size: var(--font-size-s);
}

.subtasks-controls {
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	align-items: baseline;
	justify-content: space-between;
	column-gap: 16px;
	padding-top: 8px;
}

.subtasks-add-buttons, .subtasks-connect {
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	align-items: baseline;
	column-gap: 8px;

	& .fi {
		width: 18px;
	}
}

.subtask-del {
	width: 18px;

	& > .fi {
		opacity: 0.5;
		transition: opacity 500ms;
	}

	tr:hover > & > .fi {
		--link-fg: var(--orange);
		--link-fg-hover: var(--orange);
		opacity: 1;
	}
}
</style>